/** Public portal copy (namespace `portal`). */
const en = {
  nav: { home: "Home", registry: "Policy registry", consultations: "Consultations", verify: "Verify a document", about: "About consultations" },
  uaePass: "Sign in with UAE PASS",
  uaePassSoon: "Signing in with UAE PASS will be available soon. You can respond to consultations without signing in.",
  footer: "Your responses help federal entities design better policies. Personal data is processed in line with UAE Federal Decree-Law No. 45 of 2021 on the Protection of Personal Data.",
  home: {
    eyebrow: "UAE Government",
    title: "Federal policies, open to everyone",
    intro: "Browse the national registry of approved federal policies, read drafts that are open for public consultation and tell the responsible ministry what you think, or check that a policy academy certificate is genuine.",
    browseRegistry: "Browse the registry",
    seeConsultations: "See open consultations",
    cards: {
      registry: { title: "National policy registry", body: "Approved federal policies, laws and resolutions, with their owner, status and key dates." },
      consultations: { title: "Public consultations", body: "Read draft policies and send your views before they are finalised." },
      verify: { title: "Verify a document", body: "Confirm that a Policy Academy certificate or a sealed policy dossier is genuine and has not been altered." },
    },
    openConsultations: "Open consultations",
    allConsultations: "All consultations",
    latest: "Recently published policies",
    allRegistry: "Full registry",
  },
  list: {
    title: "Public consultations",
    description: "Read draft federal policies and tell the responsible ministry what you think before they are finalised.",
    open: "Open consultations",
    closesIn: "Closes {{relative}}",
    questions_one: "{{count}} question",
    questions_other: "{{count}} questions",
    respond: "Read and respond",
    emptyTitle: "No consultations are open at the moment",
    emptyBody: "New consultations are announced here. Please check again soon.",
  },
  detail: {
    back: "All consultations",
    purpose: "Purpose of the consultation",
    policy: "Policy",
    period: "Consultation period",
    summary: "Summary of the draft",
    documents: "Consultation documents",
    noDocuments: "No documents are attached to this consultation.",
    respondTitle: "Your response",
    respondIntro: "Only your overall position, your views and your consent are needed. You may respond without giving your name.",
    name: "Name (optional)",
    organisation: "Organisation (optional)",
    organisationHint: "If you respond on behalf of a company, association or other body.",
    stance: "Overall position",
    stances: {
      support: "I support the proposal",
      support_with_changes: "I support it with changes",
      oppose: "I oppose the proposal",
      neutral: "I have no overall position",
    },
    clause: "Part of the draft you are commenting on (optional)",
    clauseHint: "Choose a clause if your comments concern a specific part of the draft.",
    clauseNone: "The draft as a whole",
    questions: "Questions from the ministry",
    body: "Your views",
    bodyHint: "Explain your position and any changes you would suggest.",
    bodyHintQuestions: "Anything you would like to add. Required if you have not answered any question.",
    bodyRequired: "Share your views, or answer at least one question.",
    consent: "I agree that my response may be analysed and published in summary form without my name.",
    privacyNote: "We do not ask for your email address. Your name and organisation are seen only by the ministry and are never published.",
    submit: "Submit response",
    submitting: "Submitting…",
    stanceRequired: "Choose your overall position.",
    consentRequired: "Your consent is needed to submit a response.",
    thanksTitle: "Thank you for your response",
    thanksBody: "Your response has been received by the responsible ministry. A summary of all responses will be published after the consultation closes.",
    closed: "This consultation is closed and no longer accepts responses.",
    notYetOpen: "This consultation opens on {{date}}. You can read the draft now and respond once it opens.",
    notFoundTitle: "Consultation not found",
    notFoundBody: "The consultation may have closed or the address may be mistyped.",
    failureTitle: {
      rateLimited: "Please wait a few minutes",
      closed: "Responses are no longer accepted",
      unavailable: "The service is not available",
      server: "Please check your response",
      generic: "Your response was not sent",
    },
    failure: {
      rateLimited: "Several responses were sent from your connection in a short time. Your text is still here: please try again in about ten minutes.",
      closed: "This consultation has closed or is not open yet, so your response could not be accepted.",
      unavailable: "We could not reach the consultation service. Your text is still here: check your connection and try again.",
      server: "Some information in your response needs to be corrected.",
      generic: "Something went wrong while sending your response. Your text is still here: please try again.",
    },
  },
  draft: {
    title: "Draft under consultation",
    showAll_one: "Show the full draft ({{count}} clause)",
    showAll_other: "Show the full draft ({{count}} clauses)",
    showText_one: "Show the full text ({{count}} clause)",
    showText_other: "Show the full text ({{count}} clauses)",
    showLess: "Show less",
  },
  registry: {
    title: "National policy registry",
    description: "Federal policies approved by the Cabinet and published for the public, with their responsible ministry and key dates.",
    search: "Search",
    searchPlaceholder: "Title, code or keyword",
    sector: "Sector",
    allSectors: "All sectors",
    instrument: "Type of instrument",
    allInstruments: "All types",
    owner: "Responsible entity",
    allOwners: "All entities",
    year: "Year",
    allYears: "All years",
    results_one: "{{total}} policy",
    results_other: "{{total}} policies",
    resultsLoading: "Searching the registry…",
    emptyTitle: "No policies have been published yet",
    emptyBody: "Approved public policies appear here once they are published.",
    noMatchTitle: "No policies match your search",
    noMatchBody: "Try other words or remove a filter.",
    clear: "Clear search and filters",
    pagination: "Registry pages",
    previous: "Previous",
    next: "Next",
    pageOf: "Page {{page}} of {{pages}}",
    view: "View policy",
    effective: "In force since {{date}}",
    publishedOn: "Published {{date}}",
    back: "Policy registry",
    notFoundTitle: "Policy not found",
    notFoundBody: "The policy may not be public or the address may be mistyped.",
    text: "Text of the policy",
    noText: "The full text of this policy is not published in the registry.",
    statuses: { published: "Published", in_force: "In force", repealed: "Repealed", expired: "Expired", superseded: "Superseded" },
    field: {
      code: "Reference code",
      owner: "Responsible entity",
      status: "Status",
      decision: "Cabinet decision",
      effective: "Effective date",
      review: "Next review due",
      sunset: "Sunset date",
      published: "Published in the registry",
    },
  },
  verify: {
    title: "Verify a document",
    description: "Check that a Policy Academy certificate, or a policy dossier sealed for the General Secretariat of the Cabinet, is genuine and has not been altered.",
    number: "Certificate or seal number",
    hint: "Printed on the document, for example GSOC-ACAD-2026-1A2B3C4D or GSOC-SEAL-2026-1A2B3C4D5E.",
    submit: "Verify",
    required: "Enter the certificate or seal number.",
    format: "The number should look like GSOC-ACAD-2026-1A2B3C4D or GSOC-SEAL-2026-1A2B3C4D5E.",
    seal: {
      validTitle: "This dossier is sealed and intact",
      validBody: "The seal is on record and the dossier has not changed since it was sealed.",
      mismatchTitle: "This document does not match the seal",
      mismatchBody: "The seal is on record, but the fingerprint on your document is different. The document may have been changed after sealing, so do not rely on it.",
      notFoundTitle: "No sealed dossier with this number",
      notFoundBody: "We could not find seal {{number}}. Check the number and try again.",
      tamperedTitle: "This seal could not be verified",
      tamperedBody: "A seal with this number exists, but the dossier no longer matches its fingerprint. Do not rely on it, and contact the General Secretariat of the Cabinet.",
      confidential: "Verification shows only the details of the seal, never the policy itself.",
      field: { number: "Seal number", ministry: "Submitted by", code: "Policy reference", sealed: "Sealed on", round: "Submission round", status: "Status", document: "Your document", text: "Policy text" },
      match: {
        yes: "Matches the sealed dossier",
        textYes: "Is the version that was sealed",
        no: "Does not match",
        unchecked: "Not compared. Scan the code or open the link printed on the document to compare it.",
      },
      status: {
        received: "Received by the General Secretariat",
        inReview: "Under review",
        challengeReview: "Under challenge review",
        endorsed: "Endorsed",
        endorsedWithConditions: "Endorsed with conditions",
        returned: "Returned to the ministry",
        notSupported: "Not supported",
        referredToRegLab: "Referred to the Regulations Lab",
        onAgenda: "On the Cabinet agenda",
        decided: "Decided by the Cabinet",
      },
    },
    validTitle: "This certificate is genuine",
    validBody: "It was issued by the Policy Academy and its details match the record.",
    notFoundTitle: "No certificate with this number",
    notFoundBody: "We could not find certificate {{number}}. Check the number and try again.",
    tamperedTitle: "This certificate could not be verified",
    tamperedBody: "A certificate with this number exists, but its record no longer matches its security seal. Do not rely on it, and contact the issuing entity.",
    field: { number: "Certificate number", holder: "Awarded to", path: "Learning path", ministry: "Entity", issued: "Issued on" },
  },
  about: {
    title: "About public consultations",
    intro: "Public consultation is part of the federal policy lifecycle. Before a policy is submitted to the Cabinet, the responsible ministry may publish a draft and invite comments from the public, businesses and civil society.",
    howTitle: "How it works",
    how: [
      "The ministry publishes the draft policy, the questions it wants answered and the closing date.",
      "You read the documents and send your response, anonymously or with your name and organisation.",
      "After the consultation closes, the ministry analyses all responses and publishes a summary of what it heard and how it will respond.",
    ],
    privacyTitle: "Your data",
    privacy: "Responses are used only to develop the policy concerned. We do not collect email addresses, and names are never published. Data is processed in line with UAE Federal Decree-Law No. 45 of 2021 on the Protection of Personal Data.",
  },
};

export default en;
export type PortalCatalog = typeof en;
